<template>
  <div v-if="available" class="card mb-3" data-test="live-location">
    <div class="card-body p-3">
      <label class="form-check form-switch m-0">
        <input class="form-check-input" type="checkbox" :checked="sharing"
               data-test="live-location-toggle" @change="toggle($event.target.checked)">
        <span class="form-check-label fw-bold">{{ t.toggle }}</span>
      </label>
      <div v-if="sharing && !geoError" class="text-success small mt-2" data-test="live-location-active">
        <span class="live-dot" aria-hidden="true"></span>{{ t.active }}
        <span v-if="lastSentAt" class="text-muted"> · {{ t.lastSent }} {{ sentClock }}</span>
      </div>
      <div v-if="geoError" class="text-danger small mt-2" data-test="live-location-error">{{ geoError }}</div>
      <div class="text-muted small mt-2" data-test="live-location-help">{{ t.help }}</div>
    </div>
  </div>
</template>

<script setup>
/**
 * Opt-in live location for the coach ETA (backlog TL-09, Q14 "Live via
 * travelling rep's phone"). Shown to a travelling rep (or a manager) while the
 * coach is between departed and arrived, and only when the
 * transfer-live-tracking flag is on (the API 404s otherwise).
 *
 * Nothing is read or sent until the rep turns the toggle on. The choice is
 * remembered per run in localStorage. While on, watchPosition runs and a
 * position is posted at most once a minute; it stops when the toggle goes off,
 * the component unmounts (arrival or leaving the page), the page is closed, or
 * the API says the coach is no longer in transit.
 */
import {ref, computed, onMounted, onBeforeUnmount} from 'vue'
import {api} from '@/services/api.js'

const props = defineProps({
  weekId: {type: [String, Number], required: true},
  transferRef: {type: String, required: true},
  direction: {type: String, required: true}
})

const MIN_POST_INTERVAL_MS = 60 * 1000
const STORAGE_PREFIX = 'liveLocation:run:'

const dict = typeof __DICT__ !== 'undefined' ? (__DICT__.liveLocation || {}) : {}
const t = {
  toggle: 'Share live location for ETA',
  active: 'Sharing location',
  lastSent: 'last sent',
  help: 'Your location is sent about once a minute, only for this coach and only until it arrives, so the office can give an accurate ETA. On iPhone it only works while the app is open on screen: locking the phone or switching apps pauses it.',
  denied: 'Location permission was refused. Allow location for this app in your phone settings to share it.',
  unavailable: 'Your location is not available right now. It will be sent when it is.',
  unsupported: 'This device cannot share its location.',
  ...dict
}

const runId = ref(null)
const available = ref(false)
const sharing = ref(false)
const geoError = ref(null)
const lastSentAt = ref(null)
let watchId = null
let lastPostAt = 0

const sentClock = computed(() => lastSentAt.value
  ? new Date(lastSentAt.value).toLocaleTimeString([], {hour: '2-digit', minute: '2-digit'})
  : '')

const storageKey = () => `${STORAGE_PREFIX}${runId.value}`
function rememberedOn() {
  try { return localStorage.getItem(storageKey()) === 'on' } catch { return false }
}
function remember(on) {
  try { localStorage.setItem(storageKey(), on ? 'on' : 'off') } catch { /* private mode */ }
}

const geolocation = () => (typeof navigator !== 'undefined' ? navigator.geolocation : null)

function start() {
  if (watchId != null) return
  const geo = geolocation()
  if (!geo) {
    geoError.value = t.unsupported
    return
  }
  watchId = geo.watchPosition(onPosition, onGeoError, {enableHighAccuracy: true, maximumAge: 30000, timeout: 60000})
}

function stop() {
  if (watchId != null) geolocation()?.clearWatch(watchId)
  watchId = null
}

async function onPosition(position) {
  geoError.value = null
  const now = Date.now()
  if (now - lastPostAt < MIN_POST_INTERVAL_MS) return
  lastPostAt = now
  try {
    // Speed (m/s) and heading (degrees) are null when the device cannot tell;
    // the forecast on the admin map uses them when present (TL-09c).
    const {speed, heading} = position.coords
    await api.post(`/api/transfer/run/${runId.value}/position`, {
      lat: position.coords.latitude,
      lon: position.coords.longitude,
      accuracy: position.coords.accuracy,
      recordedAt: position.timestamp,
      speed: Number.isFinite(speed) && speed >= 0 ? speed : null,
      heading: Number.isFinite(heading) ? heading : null
    })
    lastSentAt.value = now
  } catch (error) {
    // Arrived, no longer allowed, or the feature switched off: stop for good.
    if ([403, 404, 409].includes(error?.status)) {
      stop()
      sharing.value = false
      available.value = false
    }
  }
}

function onGeoError(error) {
  if (error?.code === 1) {
    geoError.value = t.denied
    stop()
    sharing.value = false
    remember(false)
  } else {
    geoError.value = t.unavailable
  }
}

function toggle(on) {
  sharing.value = on
  remember(on)
  geoError.value = null
  if (on) start()
  else stop()
}

const onPageHide = () => stop()

onMounted(async () => {
  try {
    const {run} = await api.get(`/api/transfer/run/${props.weekId}/${encodeURIComponent(props.transferRef)}/${props.direction}`)
    if (!run?.runId) return
    runId.value = run.runId
    const status = await api.get(`/api/transfer/run/${run.runId}/position`)
    available.value = !!(status?.enabled && status?.canShare && status?.inTransit)
  } catch {
    available.value = false
  }
  if (!available.value) return
  window.addEventListener('pagehide', onPageHide)
  if (rememberedOn()) {
    sharing.value = true
    start()
  }
})

onBeforeUnmount(() => {
  stop()
  window.removeEventListener('pagehide', onPageHide)
})
</script>

<style scoped>
.live-dot {
  display: inline-block;
  width: 0.5rem;
  height: 0.5rem;
  margin-right: 0.375rem;
  border-radius: 50%;
  background: currentColor;
  animation: live-pulse 1.6s ease-in-out infinite;
}

@keyframes live-pulse {
  50% { opacity: 0.3; }
}
</style>
